import logger from "./logger";

// Runs once at app boot (before React renders) to clear any corrupted or
// stale localStorage values that can otherwise hang the homepage — the
// "stuck spinner / loads forever" bug users hit when an old value from a
// previous app version or a dead session is still saved in their browser.
//
// Only ever REMOVES or normalizes values that are unreadable or known-bad.
// Valid data is never touched. Every key here is re-created on demand, so
// dropping a corrupt copy is always safe.

const JSON_VALUE_KEYS = [
  "eventFilters",
  "clicked_event_id",
  "signup_form_data",
  "22night_home_interests_v1",
];

function safeJsonParse(raw) {
  if (raw === null) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

export function sanitizeLocalStorage() {
  if (typeof window === "undefined") return;
  try {
    // 1. Normalize known-bad city values (legacy bug wrote "All_cities").
    const city = localStorage.getItem("city");
    if (city === "All_cities" || city === "ALL_CITIES") {
      localStorage.removeItem("city");
    }

    // 2. Drop any JSON value that fails to parse (corruption from interrupted
    //    writes or old versions). The Home/checkout code re-writes these keys.
    for (const key of JSON_VALUE_KEYS) {
      const raw = localStorage.getItem(key);
      if (raw !== null && safeJsonParse(raw) === undefined) {
        localStorage.removeItem(key);
      }
    }

    // 3. Drop a malformed Supabase session object (unreadable, not merely
    //    expired) so auth-js starts clean instead of choking on it. Expired
    //    sessions are left alone — auth-js refreshes or clears those itself.
    const authKeys = Object.keys(localStorage).filter((k) =>
      /^sb-[a-z0-9-]+-auth-token$/.test(k)
    );
    for (const key of authKeys) {
      const raw = localStorage.getItem(key);
      if (raw === null) continue;
      const session = safeJsonParse(raw);
      if (
        session === undefined ||
        session === null ||
        typeof session !== "object" ||
        !("access_token" in session)
      ) {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    // Sanitization must never throw or block boot.
    logger.warn("localStorage sanitizer skipped an error:", error);
  }
}
